/* Task Center — a tiny global store for long-running async jobs (e.g. report
 * CSV generation) so they keep running independent of the screen that started
 * them and show progress in the topbar notification bell.
 *
 * Usage:
 *   const { runTask } = useTaskCenter();
 *   runTask({
 *     title: "Out of Stuck Report",
 *     run: async (onProgress) => {            // runs to completion regardless of navigation
 *       onProgress("Generating…");
 *       ...
 *       return { detail: "Downloaded", action: { label: "Download again", run: () => ... } };
 *     },
 *   });
 */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Bell, Loader2, Check, AlertTriangle, Download, X } from "lucide-react";

const TaskCenterContext = createContext(null);

export const useTaskCenter = () => {
  const ctx = useContext(TaskCenterContext);
  if (!ctx) throw new Error("useTaskCenter must be used within <TaskCenterProvider>");
  return ctx;
};

export const TaskCenterProvider = ({ children }) => {
  const [tasks, setTasks] = useState([]);
  const idRef = useRef(0);

  const patch = useCallback((id, p) => {
    setTasks((ts) => ts.map((t) => (t.id === id ? { ...t, ...p } : t)));
  }, []);

  const removeTask = useCallback((id) => {
    setTasks((ts) => ts.filter((t) => t.id !== id));
  }, []);

  const clearFinished = useCallback(() => {
    setTasks((ts) => ts.filter((t) => t.status === "running"));
  }, []);

  /* Kicks off `run`, tracking it as a task. `run` receives an onProgress(detail)
     callback and may return { detail, action } on success. The promise is not
     tied to any screen, so it survives navigation. */
  const runTask = useCallback(
    ({ title, run }) => {
      const id = (idRef.current += 1);
      setTasks((ts) => [{ id, title, status: "running", detail: "Starting…", action: null, seen: false }, ...ts]);
      Promise.resolve()
        .then(() => run((detail) => patch(id, { detail })))
        .then((res) =>
          patch(id, { status: "done", detail: (res && res.detail) || "Completed", action: (res && res.action) || null, seen: false })
        )
        .catch((e) => patch(id, { status: "failed", detail: e?.message || "Failed", seen: false }));
      return id;
    },
    [patch]
  );

  return (
    <TaskCenterContext.Provider value={{ tasks, runTask, removeTask, clearFinished, markAllSeen: () => setTasks((ts) => ts.map((t) => ({ ...t, seen: true }))) }}>
      {children}
    </TaskCenterContext.Provider>
  );
};

/* The topbar bell + dropdown. Lives inside the provider. */
export const TaskCenterBell = () => {
  const { tasks, removeTask, clearFinished, markAllSeen } = useTaskCenter();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const running = tasks.filter((t) => t.status === "running").length;
  const unseen = tasks.filter((t) => !t.seen).length;
  const badge = running || unseen;

  const toggle = () => {
    setOpen((o) => {
      const next = !o;
      if (next) markAllSeen();
      return next;
    });
  };

  return (
    <div className="relative" ref={ref}>
      <button
        title="Notifications"
        onClick={toggle}
        className="relative w-8 h-8 flex items-center justify-center rounded-cb-sm text-fg-muted hover:bg-surface-hover hover:text-fg-strong transition-colors"
      >
        <Bell size={17} strokeWidth={1.6} />
        {badge > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[15px] h-[15px] px-1 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center">
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute top-[40px] right-0 w-80 max-w-[calc(100vw-16px)] bg-panel border border-hairline-subtle rounded-cb-md shadow-cb-md z-50 cb-animate-in overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b border-hairline-subtle">
            <span className="text-t-sm font-semibold text-fg-strong">Notifications</span>
            {tasks.some((t) => t.status !== "running") && (
              <button
                onClick={clearFinished}
                className="text-[11px] font-semibold text-fg-link hover:underline"
              >
                Clear finished
              </button>
            )}
          </div>

          {tasks.length === 0 ? (
            <div className="px-3 py-6 text-center text-t-sm text-fg-muted">No notifications</div>
          ) : (
            <div className="max-h-80 overflow-auto cb-scroll">
              {tasks.map((t) => (
                <TaskRow key={t.id} task={t} onDismiss={() => removeTask(t.id)} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const STATUS_META = {
  running: { icon: <Loader2 size={15} className="animate-spin" />, color: "var(--fg-link)" },
  done: { icon: <Check size={15} />, color: "var(--green-700)" },
  failed: { icon: <AlertTriangle size={15} />, color: "var(--red-700)" },
};

const TaskRow = ({ task, onDismiss }) => {
  const meta = STATUS_META[task.status] || STATUS_META.running;
  const [busy, setBusy] = useState(false);
  const onAction = async () => {
    if (!task.action || busy) return;
    setBusy(true);
    try {
      await task.action.run();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-start gap-2.5 px-3 py-2.5 border-b border-hairline-subtle last:border-0">
      <span className="mt-0.5 shrink-0" style={{ color: meta.color }}>
        {meta.icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-t-sm font-semibold text-fg-strong truncate">{task.title}</div>
        <div className="text-[11px] text-fg-muted mt-0.5">{task.detail}</div>
        {task.status === "done" && task.action && (
          <button
            onClick={onAction}
            disabled={busy}
            className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-fg-link hover:underline disabled:opacity-50"
          >
            <Download size={12} strokeWidth={1.9} />
            {busy ? "Downloading…" : task.action.label}
          </button>
        )}
      </div>
      {task.status !== "running" && (
        <button
          onClick={onDismiss}
          title="Dismiss"
          className="shrink-0 text-fg-subtle hover:text-fg-strong"
        >
          <X size={13} />
        </button>
      )}
    </div>
  );
};
